import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function PresentValueCalculator() {
  const [futureValue, setFutureValue] = useState("");
  const [discountRate, setDiscountRate] = useState("");
  const [periods, setPeriods] = useState("");
  const [result, setResult] = useState<number | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Finance", url: "https://calclab.app/finance" },
      { name: "Present Value Calculator", url: "https://calclab.app/calculator/present-value" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Present Value Calculator",
      "Calculate current value of future cash flows. Free online finance calculator with instant results and comprehensive analysis.",
      "https://calclab.app/calculator/present-value",
      lang
    )
  ]);

  const calculate = () => {
    const FV = parseFloat(futureValue);
    const r = parseFloat(discountRate) / 100;
    const n = parseFloat(periods);

    if (isNaN(FV) || isNaN(r) || isNaN(n) || FV <= 0 || r < 0 || n <= 0) {
      toast.error("Please enter valid values");
      return;
    }

    const PV = FV / Math.pow(1 + r, n);
    setResult(PV);
    toast.success("Present value calculated!");
  };

  const reset = () => {
    setFutureValue("");
    setDiscountRate("");
    setPeriods("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Present Value Calculator</CardTitle>
          <CardDescription>
            Calculate current value of future cash flows: PV = FV / (1 + r)^n
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="futureValue">Future Value ($)</Label>
            <Input
              id="futureValue"
              type="number"
              placeholder="15000"
              value={futureValue}
              onChange={(e) => setFutureValue(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="discountRate">Discount Rate (% per period)</Label>
            <Input
              id="discountRate"
              type="number"
              placeholder="5"
              value={discountRate}
              onChange={(e) => setDiscountRate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="periods">Number of Periods</Label>
            <Input
              id="periods"
              type="number"
              placeholder="10"
              value={periods}
              onChange={(e) => setPeriods(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-lg break-all overflow-wrap-anywhere">
                  <strong>Present Value:</strong> ${result.toFixed(2)}
                </p>
                <CopyResultButton value={result.toFixed(2)} />
              </div>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["present-value"]} trigger={result} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
